import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { productsApi } from "../api/products";
import type { ValidationIssue } from "../api/types";
import { ErrorNotice, Panel } from "../components/ui";

/** Which section of the workspace an issue belongs to.
 *
 * Grouping is derived from the backend's own `field`, so a new issue code lands
 * somewhere sensible without this file being edited. */
const GROUPS: { key: string; label: string; fields: string[] }[] = [
  { key: "details", label: "Product details", fields: ["name", "base_price", "sale_price", "currency", "category"] },
  { key: "variants", label: "Colors", fields: ["variants"] },
  { key: "images", label: "Images", fields: ["images"] },
  { key: "sources", label: "Sources and processing", fields: ["sources", "processing"] },
];

function groupFor(issue: ValidationIssue): string {
  for (const group of GROUPS) {
    if (issue.field && group.fields.includes(issue.field)) return group.key;
  }
  return "general";
}

export function ValidationPanel({
  productId,
  canValidate,
}: {
  productId: string;
  canValidate: boolean;
}) {
  // Validation is run on demand, but once it has been run it becomes a live
  // query: every image, variant or metadata mutation invalidates this key, so
  // the issue list follows the draft instead of going quietly out of date.
  //
  // Whether it has been run is seeded from the cache rather than kept purely in
  // component state, because this panel unmounts whenever another tab is shown.
  // Local state alone would reset on every tab switch and silently leave a
  // stale verdict on screen.
  const client = useQueryClient();
  const key = ["validation", productId];
  const [hasRun, setHasRun] = useState(
    () => client.getQueryData(key) !== undefined,
  );

  const validation = useQuery({
    queryKey: key,
    queryFn: () => productsApi.validateDraft(productId),
    enabled: hasRun && canValidate,
    staleTime: 0,
  });

  const result = validation.data ?? null;
  const error = validation.error;

  return (
    <Panel
      title="Validation"
      actions={
        <button
          type="button"
          className="btn btn--sm"
          disabled={!canValidate || validation.isFetching}
          onClick={() => {
            if (!hasRun) setHasRun(true);
            else void validation.refetch();
          }}
        >
          {validation.isFetching ? "Checking…" : "Run validation"}
        </button>
      }
    >
      {error ? <ErrorNotice error={error} /> : null}

      {!result ? (
        <p className="muted">
          {canValidate
            ? "Run validation to see what still needs attention before publishing."
            : "Open a draft to validate it."}
        </p>
      ) : (
        <div className="stack">
          <div
            className={
              result.publishable ? "notice notice--success" : "notice notice--error"
            }
            role="status"
          >
            {result.publishable
              ? "This draft is ready to publish."
              : `This draft cannot be published yet — ${result.blocking.length} issue${
                  result.blocking.length === 1 ? "" : "s"
                } must be resolved.`}
            {result.warnings.length ? (
              <>
                {" "}
                {result.warnings.length} warning
                {result.warnings.length === 1 ? "" : "s"} will not block publishing.
              </>
            ) : null}
          </div>

          <IssueList
            title="Must be fixed"
            issues={result.blocking}
            severity="blocking"
          />
          <IssueList
            title="Worth reviewing"
            issues={result.warnings}
            severity="warning"
          />
        </div>
      )}
    </Panel>
  );
}

function IssueList({
  title,
  issues,
  severity,
}: {
  title: string;
  issues: ValidationIssue[];
  severity: "blocking" | "warning";
}) {
  if (issues.length === 0) return null;

  const grouped = new Map<string, ValidationIssue[]>();
  for (const issue of issues) {
    const key = groupFor(issue);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(issue);
    else grouped.set(key, [issue]);
  }

  const order = [...GROUPS.map((g) => g.key), "general"];
  const labels = new Map(GROUPS.map((g) => [g.key, g.label]));
  labels.set("general", "General");

  return (
    <div>
      <h3 className="issue-group__title">{title}</h3>
      {order
        .filter((key) => grouped.has(key))
        .map((key) => (
          <div className="issue-group" key={key}>
            <div className="issue-group__title">{labels.get(key)}</div>
            {(grouped.get(key) ?? []).map((issue, index) => (
              <div
                className={`issue issue--${severity}`}
                key={`${issue.code}-${issue.entity ?? index}`}
              >
                <span className="issue__code">{issue.code}</span>
                <span>{issue.message}</span>
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}
